import type { ReactNode } from "react";
import { cn } from "../lib/cn.js";

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
  className
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <header className={cn("mb-5 flex flex-wrap items-start justify-between gap-4", className)}>
      <div className="min-w-0">
        {eyebrow && <div className="mb-1 text-xs font-medium text-text-muted">{eyebrow}</div>}
        <h1 className="text-[22px] font-semibold leading-8 text-text-primary">{title}</h1>
        {description && <p className="mt-1 max-w-3xl text-sm leading-5 text-text-secondary">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center justify-end gap-2">{actions}</div>}
    </header>
  );
}

export function MetricCard({
  label,
  value,
  detail,
  icon,
  tone = "info",
  className
}: {
  label: ReactNode;
  value: ReactNode;
  detail?: ReactNode;
  icon?: ReactNode;
  tone?: "info" | "success" | "warning" | "danger" | "neutral";
  className?: string;
}) {
  const toneClass = {
    info: "bg-accent-subtle text-accent",
    success: "bg-status-success/10 text-status-success",
    warning: "bg-status-warning/10 text-status-warning",
    danger: "bg-status-danger/10 text-status-danger",
    neutral: "bg-surface-mute text-text-secondary"
  }[tone];
  return (
    <article className={cn("rounded border border-surface-border bg-surface-panel px-4 py-3.5", className)}>
      <div className="flex items-start justify-between gap-3">
        <div className="text-xs font-medium text-text-secondary">{label}</div>
        {icon && <div className={cn("grid h-8 w-8 shrink-0 place-items-center rounded", toneClass)}>{icon}</div>}
      </div>
      <div className="mt-2 text-2xl font-semibold leading-8 text-text-primary">{value}</div>
      {detail && <div className="mt-1 text-xs text-text-muted">{detail}</div>}
    </article>
  );
}

export function SectionSurface({
  children,
  className
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={cn("rounded border border-surface-border bg-surface-panel", className)}>{children}</div>;
}

export function SectionHeader({
  title,
  description,
  actions
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 border-b border-surface-border px-4 py-3">
      <div className="min-w-0">
        <h2 className="text-sm font-semibold text-text-primary">{title}</h2>
        {description && <p className="mt-0.5 text-xs leading-5 text-text-secondary">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center justify-end gap-2">{actions}</div>}
    </div>
  );
}
